// Risk History Navigation Panel Component
import React, { useState } from 'react';

const RiskHistoryPanel = ({ 
  historyHook,
  currentPlayerAddress 
}) => {
  const {
    loading,
    error,
    turnHistory,
    totalTurns,
    currentTurnIndex,
    isViewingHistory,
    navigateToTurn,
    goToCurrentState,
    goToPreviousTurn,
    goToNextTurn,
    goToFirstTurn,
    goToLastTurn,
    getCurrentViewState,
    getRecentActivity,
    getTurnDetails,
    canGoBack,
    canGoForward,
    hasHistory
  } = historyHook;

  const [showDetails, setShowDetails] = useState(false);
  const [showRecentActivity, setShowRecentActivity] = useState(true);

  const currentView = getCurrentViewState();
  const recentActivity = getRecentActivity(3);

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  // Format player display
  const formatPlayer = (playerNum, gameState) => {
    if (!playerNum || !gameState) return 'Unknown';
    const isCurrentPlayer = (playerNum === 1 && gameState.player1 === currentPlayerAddress) ||
                           (playerNum === 2 && gameState.player2 === currentPlayerAddress);
    return `Player ${playerNum}${isCurrentPlayer ? ' (You)' : ''}`;
  };

  // Get turn details for current view
  const currentTurnDetails = currentTurnIndex >= 0 ? getTurnDetails(currentTurnIndex) : null;

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h3 className="text-xl font-semibold mb-3 text-gray-700">Game History</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading game history...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h3 className="text-xl font-semibold mb-3 text-gray-700">Game History</h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Error loading history: {error}</p>
        </div>
      </div>
    );
  }

  if (!hasHistory) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h3 className="text-xl font-semibold mb-3 text-gray-700">Game History</h3>
        <p className="text-gray-600">No game history available yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <h3 className="text-xl font-semibold mb-4 text-gray-700">Game History</h3>
      
      {/* Current View Status */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-blue-600 font-medium">Currently Viewing</p>
            <p className="text-lg font-semibold text-blue-800">{currentView.displayName}</p>
            {isViewingHistory && currentTurnDetails && (
              <p className="text-sm text-blue-600 mt-1">
                {formatPlayer(currentTurnDetails.player, currentTurnDetails.stateBefore)} • {formatTimestamp(currentTurnDetails.timestamp)}
              </p>
            )}
          </div>
          {isViewingHistory && (
            <button
              onClick={goToCurrentState}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              Back to Live
            </button>
          )}
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600 font-medium">Navigate History</p>
          <p className="text-xs text-gray-500">{totalTurns} turns total</p>
        </div>
        
        {/* Navigation Buttons */}
        <div className="flex items-center space-x-2 mb-4">
          <button
            onClick={goToFirstTurn}
            disabled={!canGoBack}
            className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="First Turn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            onClick={goToPreviousTurn}
            disabled={!canGoBack}
            className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Previous Turn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex-1 mx-4">
            <div className="relative">
              <input
                type="range"
                min="-1"
                max={totalTurns - 1}
                value={currentTurnIndex}
                onChange={(e) => navigateToTurn(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Current</span>
                <span>Turn {totalTurns}</span>
              </div>
            </div>
          </div>

          <button
            onClick={goToNextTurn}
            disabled={!canGoForward}
            className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Next Turn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          <button
            onClick={goToLastTurn}
            disabled={!canGoForward}
            className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Last Turn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Quick Jump Buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={goToCurrentState}
            className={`px-3 py-1 text-xs rounded-full border ${
              currentTurnIndex === -1 
                ? 'bg-blue-500 text-white border-blue-500' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Current
          </button>
          {turnHistory.slice(-5).map((turn, idx) => {
            const turnIdx = turnHistory.length - 5 + idx;
            return (
              <button
                key={turnIdx}
                onClick={() => navigateToTurn(turnIdx)}
                className={`px-3 py-1 text-xs rounded-full border ${
                  currentTurnIndex === turnIdx
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                T{turn.turnNumber}
              </button>
            );
          })}
        </div>
      </div>

      {/* Turn Details */}
      {isViewingHistory && currentTurnDetails && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-800">Turn {currentTurnDetails.turnNumber} Details</h4>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showDetails ? 'Hide' : 'Show'} Details
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Player</p>
              <p className="font-medium">{formatPlayer(currentTurnDetails.player, currentTurnDetails.stateBefore)}</p>
            </div>
            <div>
              <p className="text-gray-600">Moves</p>
              <p className="font-medium">{currentTurnDetails.summary.numMoves}</p>
            </div>
            <div>
              <p className="text-gray-600">Combats</p>
              <p className="font-medium">{currentTurnDetails.summary.numCombats}</p>
            </div>
            <div>
              <p className="text-gray-600">Conquests</p>
              <p className="font-medium">{currentTurnDetails.summary.numConquests}</p>
            </div>
          </div>

          {showDetails && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h5 className="font-medium text-gray-800 mb-2">Events</h5>
              <div className="space-y-2">
                {currentTurnDetails.events.map((event, idx) => (
                  <div key={idx} className="text-sm bg-white p-2 rounded border">
                    <span className="font-medium text-blue-600">{event.type}</span>
                    {event.type === 'Combat' && (
                      <span className="ml-2 text-gray-600">
                        Territory {event.args.territory}: {event.args.attackers} vs {event.args.defenders} 
                        (Lost: {event.args.attackerLosses}/{event.args.defenderLosses})
                      </span>
                    )}
                    {event.type === 'TerritoryConquered' && (
                      <span className="ml-2 text-gray-600">
                        Territory {event.args.territory} conquered
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent Activity Summary */}
      {showRecentActivity && recentActivity.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-800">Recent Activity</h4>
            <button
              onClick={() => setShowRecentActivity(!showRecentActivity)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Hide
            </button>
          </div>
          
          <div className="space-y-2">
            {recentActivity.map((activity) => (
              <div key={activity.turnNumber} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    Turn {activity.turnNumber} - Player {activity.player}
                  </p>
                  <p className="text-xs text-gray-600">
                    {activity.numMoves} moves, {activity.combats} combats, {activity.conquests} conquests
                  </p>
                </div>
                <button
                  onClick={() => navigateToTurn(activity.turnNumber - 1)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  View
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskHistoryPanel;